import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminCreatedSchema,
  adminListSchema,
  adminResultSchema,
  type AdminCreated,
  type AdminList,
  type AdminResult,
  type CreateAdminInput,
  type UpdateAdminInput,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Administrateurs de la plateforme. Toutes ces routes sont réservées aux
 * superadmins : un `admin` reçoit 403.
 */
export function useAdmins(options: { enabled?: boolean } = {}) {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.admins.list(),
    queryFn: () => api.get<AdminList>('/admin/admins', { schema: adminListSchema }),
    enabled: token !== null && (options.enabled ?? true),
  });
}

/**
 * Crée un administrateur. Si aucun mot de passe n'est fourni, la réponse
 * contient celui qui a été généré : c'est la **seule** occasion de le lire.
 */
export function useCreateAdmin() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAdminInput) =>
      api.post<AdminCreated>('/admin/admins', input, { schema: adminCreatedSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.admins.all() }),
  });
}

export function useUpdateAdmin() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAdminInput & { id: string }) =>
      api.patch<AdminResult>(`/admin/admins/${id}`, input, { schema: adminResultSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.admins.all() }),
  });
}

export function useDeleteAdmin() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/admins/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.admins.all() }),
  });
}
